import loadable from '@loadable/component';
import { RouterLink } from '@redux-first-router-link';
import { useTracking } from 'react-tracking';

import { ROUTE_PORTFOLIO_NEW, ROUTE_PORTFOLIO_DEMO } from '@/constants/routes';

import { Grid, Box, Text, Button, useMount } from '@simplywallst/ui-core';

import { useTypeformDrawer } from '@/hooks/useTypeformDrawer';

import { ChartDefinitions as MiniPortfolioPerformanceChartDefinitions } from '../MiniPortfolioPerformanceChart';
import { AggregatePortfolioOverview } from '../AggregatePortfolioOverview';
import { Portfolios } from '../Portfolios';
import { LegacyPortfolios } from '../LegacyPortfolios';

import type { Portfolios as PortfoliosType } from '@/hooks/portfolio/usePortfolio';
import { getPortfoliosCountByLimits } from '@/features/portfolio/utils';

const PortfolioOnboardingSlideShow = loadable(
  () => import('@/components/PortfolioOnboardingSlideShow'),
  {
    resolveComponent: (c) => c.PortfolioOnboardingSlideShow,
  }
);

type Props = {
  portfolios: PortfoliosType;
};
export const PopulatedState = ({ portfolios }: Props) => {
  const { trackEvent } = useTracking({
    type: 'track',
    modifier: 'populatedState',
  });
  const popUpTypeForm = useTypeformDrawer('Hk37XBaI');

  const hasPortfolios = portfolios.length > 0;

  useMount(() => {
    const { fullyLimitedPortfolios, partiallyLimitedPortfolios } =
      getPortfoliosCountByLimits(portfolios);
    const totalPortfolios = portfolios.length;

    trackEvent({
      action: 'landed',
      fullyLimitedPortfolios,
      partiallyLimitedPortfolios,
      totalPortfolios,
      nonLimitedPortfolios:
        totalPortfolios - (fullyLimitedPortfolios + partiallyLimitedPortfolios),
    });
  });

  return (
    <>
      {hasPortfolios ? (
        <>
          <MiniPortfolioPerformanceChartDefinitions />
          <AggregatePortfolioOverview />

          <Box marginTop="x6">
            <Portfolios portfolios={portfolios} />
          </Box>
        </>
      ) : (
        <Box paddingY="x2">
          <PortfolioOnboardingSlideShow
            footer={
              <Grid
                templateColumnsMd="max-content max-content"
                justifyContentMd="end"
                gap="8px"
              >
                <Grid rowMd="1" columnMd="2">
                  <RouterLink to={{ type: ROUTE_PORTFOLIO_NEW }}>
                    <Button styleType="louderLarge" displaySmDown="block">
                      Create my portfolio
                    </Button>
                  </RouterLink>
                </Grid>
                <Grid rowMd="1" columnMd="1">
                  <RouterLink to={{ type: ROUTE_PORTFOLIO_DEMO }}>
                    <Button styleType="loudLarge" displaySmDown="block">
                      Try demo portfolio
                    </Button>
                  </RouterLink>
                </Grid>
              </Grid>
            }
          />
        </Box>
      )}

      <Box marginTop="x6">
        <LegacyPortfolios />
      </Box>

      <Box paddingX="x2" paddingTop="x8" paddingBottom="x4">
        <Text
          textAlign="center"
          typography="xSmall"
          color="whiteRGBA"
          opacity={0.5}
        >
          Exciting new features are on the way to help you achieve your
          portfolio goals 🚀{' '}
          <Button
            as="a"
            styleType="strongLink"
            href="/help/articles/hc/en-us/articles/7461937035279-Building-the-new-portfolio-tool-Roadmap-"
            target="_blank"
          >
            Learn more
          </Button>{' '}
          or{' '}
          <Button
            styleType="strongLink"
            display="inline"
            onClick={popUpTypeForm}
          >
            Take the survey
          </Button>
        </Text>
      </Box>
    </>
  );
};
